// src/utils/completeRegistration.ts
//
// Registrace ve dvou krocích (bezpečnost a, nálezy 1 a 2 revize).
//
// Firebase Auth založí účet e-mail/heslo i s cizím e-mailem – adresa se
// ověří až odkazem z e-mailu. Dřív registrace hned po založení účtu hledala
// importovaný profil se stejným e-mailem a převedla ho na nový účet, takže
// kdo znal e-mail importovaného člena bez účtu, přečetl a převzal jeho
// profil (rok narození, telefon, ZP, členství, číslo = variabilní symbol).
// Nově:
//  1. Registrace (AuthPage) založí jen účet Firebase Auth a údaje formuláře
//     uloží do pendingProfiles/{uid} (savePendingProfile) – žádný profil
//     users/{uid}, žádné čtení cizích profilů; pošle ověřovací e-mail
//     a odhlásí.
//  2. První přihlášení s OVĚŘENÝM e-mailem (completeRegistration): když
//     users/{uid} chybí, najde importované profily se stejným e-mailem
//     (pravidla ten dotaz pustí jen s ověřeným e-mailem) a převede na nový
//     účet ten, jehož jméno (i generační přípona ml./st.) odpovídá
//     rozpracovanému profilu a rok narození se SHODUJE (nameMatch,
//     chooseLegacy); neodpovídá-li žádný, nebo import rok narození nemá
//     (nález 1 4. kola), nic nezapíše a zeptá se střelce
//     (needs-confirmation → AuthPage → RegistrationChoice). Bez importu
//     založí nového člena s číslem z počítadla. Rozpracovaný profil se smaže
//     v TÉŽE transakci. Účty ze starého toku (profil vznikl už při
//     registraci) jen projdou kontrolou existence profilu.
//  Převod ponechá platný rok narození a pohlaví importu (v kanonickém
//  tvaru) – formulář je jen doplní, chybí-li nebo jsou neplatné
//  (lockedFromLegacy; rozhodnutí majitele 12. 10. 2026:
//  nové importy nebudou, rok narození a pohlaví mění jen správce).
//  Účet bez profilu s náhrobkem deletedProfiles/{uid} (smazání účtu se
//  nedokončilo, UpdateProfile) – i s rozpracovaným profilem – chyba
//  lks/account-deleted; bez náhrobku i bez rozpracovaného profilu
//  lks/registration-incomplete. AuthPage podle nich nabídne dokončení
//  smazání, resp. doplnění údajů (nález 10 2. kola, nález 2 3. kola).

import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDocFromCache,
  getDocFromServer,
  getDocsFromServer,
  query,
  runTransaction,
  serverTimestamp,
  where,
  type DocumentData,
  type DocumentReference,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { CONTACT_EMAIL } from "./contact";
import { ensureVerifiedToken } from "./emailVerification";
import { firebaseErrorMessage } from "./firebaseErrors";
import { isMembershipPaid } from "./membership";
import {
  createMemberProfile,
  isValidSequenceNumber,
  padSequenceNumber,
  runTransactionRetrying,
  takeSequenceNumber,
  toSequenceInt,
} from "./memberNumber";
import { formatPersonName, nameMatchQuality, nameWords } from "./nameMatch";
import { describeError, reportWarning } from "./reportError";
import { PERMANENT_SUPER_ADMIN_EMAIL } from "./superadmin";

/** Rozpracované profily z registrace (doc id = uid), firestore.rules pendingProfiles. */
export const PENDING_PROFILES_COLLECTION = "pendingProfiles";

/**
 * Náhrobky smazaných profilů (doc id = uid, { deletedAt }) – zapisuje je
 * smazání účtu v téže transakci jako smazání profilu
 * (UpdateProfile.deleteAccount). Pravidla pak vlastníkovi nový profil
 * nezaloží (nález 12: smazáním a novým založením profilu se obcházel zákaz
 * nováčka i zámek roku narození); znovu ho povolí jen správce smazáním
 * náhrobku.
 */
export const DELETED_PROFILES_COLLECTION = "deletedProfiles";

/** Údaje registračního formuláře – jen tato pole pravidla do pendingProfiles pustí. */
export interface PendingProfileInput {
  firstName: string;
  lastName: string;
  birthYear: number;
  gender: string;
  nationality: string;
  phone: string;
  /** E-mail přihlášeného účtu (pravidla: musí se rovnat e-mailu v tokenu). */
  email: string;
}

/**
 * Osobní údaje z formuláře, které profil přebírá. Převáděný import přepíšou –
 * kromě platného roku narození a pohlaví importu (lockedFromLegacy).
 */
const PERSONAL_FIELDS = [
  "firstName",
  "lastName",
  "birthYear",
  "gender",
  "nationality",
  "phone",
];

/**
 * Importovaný profil se stejným e-mailem, na který se registrace ptá
 * (needs-confirmation). Vlastník ověřeného e-mailu ho smí vidět – pravidla
 * mu tytéž profily pustí dotazem podle e-mailu.
 */
export interface LegacyCandidate {
  id: string;
  /** Jméno a příjmení z importu („Jan Novák ml.“); "" = import bez jména. */
  name: string;
  /** Rok narození z importu (rozliší stejná jména v rodině), jinak null. */
  birthYear: number | null;
  /** Platné členské číslo ("031"), jinak null. */
  sequenceNumber: string | null;
}

/**
 * Odpověď střelce na needs-confirmation: převést konkrétní import („Jsem
 * to já“), nebo založit nového člena („Nejsem nikdo z nich“).
 */
export type RegistrationChoice = { kind: "transfer"; legacyId: string } | { kind: "new" };

/** Osobní údaje, které po registraci mění jen správce (převod je bere z importu). */
export type LockedField = "birthYear" | "gender";

export type CompletionResult =
  /** Profil už byl (běžné přihlášení, účet ze starého toku, jiné zařízení). */
  | { status: "existing" }
  /** Trvalý super admin bez profilu – aplikace ho pustí i tak (utils/superadmin). */
  | { status: "superAdmin" }
  /** Nový člen s číslem z počítadla. */
  | { status: "created"; sequenceNumber: string }
  /**
   * Převedený importovaný profil (číslo původní, nebo nové, když původní
   * nebylo platné). overruled: pole, která registrace uvedla jinak, než je
   * má import – platí import (lockedFromLegacy); chybí = žádný rozdíl.
   */
  | {
      status: "transferred";
      legacyId: string;
      sequenceNumber: string;
      overruled?: LockedField[];
    }
  /**
   * K e-mailu jsou importy, jméno nebo rok narození ale nesedí s žádným
   * (nebo import rok narození nemá) – nic se nezapsalo, rozhodne střelec
   * (completeRegistration s RegistrationChoice). goneLegacyId: import
   * vybraný odpovědí „Jsem to já“, který mezitím z evidence zmizel
   * (sloučil ho správce, změnil mu e-mail) – candidates jsou čerstvé, mohou
   * být i prázdné.
   */
  | {
      status: "needs-confirmation";
      candidates: LegacyCandidate[];
      goneLegacyId?: string;
    };

/** Krok dokončení, ve kterém nastala chyba (hláška pro uživatele). */
export type CompletionStep =
  | "profile"
  | "token"
  | "pending"
  | "legacy"
  | "create"
  | "transfer";

/** Kód chyby Firebase / vlastní chyby ("permission-denied", "lks/…"), jinak "". */
const errorCode = (err: unknown): string => {
  const code =
    err && typeof err === "object" ? (err as { code?: unknown }).code : undefined;
  return typeof code === "string" ? code : "";
};

/** Chyba s vlastním kódem (firebaseErrorMessage podle něj najde hlášku). */
const codedError = (code: string, message: string): Error & { code: string } =>
  Object.assign(new Error(message), { code });

/**
 * Chyba dokončení registrace: kód původní chyby (firebaseErrorMessage podle
 * něj najde hlášku) a krok, ve kterém nastala.
 */
export class RegistrationCompletionError extends Error {
  readonly code: string;

  constructor(readonly step: CompletionStep, readonly original: unknown) {
    super(describeError(original));
    this.name = "RegistrationCompletionError";
    this.code = errorCode(original);
  }
}

/** Krok z chyby dokončení (jiné chyby → null). */
const stepOf = (err: unknown): CompletionStep | null => {
  const step =
    err && typeof err === "object" ? (err as { step?: unknown }).step : undefined;
  return typeof step === "string" ? (step as CompletionStep) : null;
};

/** Spustí krok; chybu obalí krokem (RegistrationCompletionError). */
async function inStep<T>(step: CompletionStep, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    throw new RegistrationCompletionError(step, err);
  }
}

/** Obecná hláška – opakování (nové přihlášení) obvykle pomůže. */
const RETRY_MESSAGE = "Registraci se nepodařilo dokončit, zkuste to prosím znovu.";

/** Česká hláška pro chybu z completeRegistration (AuthPage – formulář přihlášení). */
export function completionErrorMessage(err: unknown): string {
  if (errorCode(err) === "permission-denied") {
    // Převod importu pravidla odmítla i po opakování (záloha toho záznamu už
    // existuje, import se mezitím změnil…) – další pokus nepomůže a „nemáte
    // oprávnění" by mátlo. Jinde jde o prohraný souběh: zkusit znovu.
    return stepOf(err) === "transfer"
      ? `K tomuto e-mailu už evidujeme člena klubu, jeho záznam se ale ` +
          `nepodařilo převést na nový účet. Napište nám prosím na ${CONTACT_EMAIL}.`
      : RETRY_MESSAGE;
  }
  return firebaseErrorMessage(err, RETRY_MESSAGE);
}

/**
 * Hláška, když se rozpracovaný profil neuloží (registrace, doplnění údajů
 * v AuthPage). permission-denied tu znamená pravidla bez bloku
 * pendingProfiles (nasazení v nesprávném pořadí, nález 9 2. kola) – to
 * uživatel nespraví a „nemáte oprávnění“ by mátlo.
 */
export function pendingSaveErrorMessage(err: unknown, fallback: string): string {
  if (errorCode(err) === "permission-denied") {
    return `Registraci teď nejde dokončit – zkuste to prosím později nebo napište správci klubu na ${CONTACT_EMAIL}.`;
  }
  return firebaseErrorMessage(err, fallback);
}

/**
 * Importovaný profil, který si nový účet smí převzít: role chybí, '' nebo
 * 'user' (jako firestore.rules – data.get('role', '') in ['', 'user']).
 * Profil správce pravidla převést nedovolí. Používají ho i Kontroly
 * integrity (AdminOverview – importy bez roku narození).
 */
export const isTransferableProfile = (data: DocumentData): boolean =>
  !("role" in data) || data.role === "" || data.role === "user";

/** Nejnovější rok zaplaceného členství importu (0 = žádné). */
const latestPaidYear = (data: DocumentData): number => {
  const membership = data.membership;
  if (!membership || typeof membership !== "object") return 0;
  let latest = 0;
  Object.keys(membership).forEach((key) => {
    const year = /^\d{4}$/.test(key) ? parseInt(key, 10) : 0;
    if (year > latest && isMembershipPaid(data, key)) latest = year;
  });
  return latest;
};

/**
 * Použitelný rok narození: celé číslo nebo čtyřmístný text (i s mezerami
 * okolo), jak ho mají importy, v rozsahu 1900–2100. Chybí, 0, "", datum
 * („2010-04-12“), neceločíselný nebo jiný nesmysl → null (neznámý) – podle
 * takového roku se import bez dotazu nepřevede (chooseLegacy). Stejně ho
 * čtou Kontroly integrity (AdminOverview – importy bez roku narození).
 */
export const usableBirthYear = (value: unknown): number | null => {
  const year =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\s*\d{4}\s*$/.test(value)
      ? parseInt(value, 10)
      : NaN;
  return Number.isInteger(year) && year >= 1900 && year <= 2100 ? year : null;
};

/**
 * Použitelné pohlaví: „muž“ nebo „žena“ bez ohledu na velikost písmen
 * a okrajové mezery (importy mívají „MUŽ“, „Žena“) → "muž" / "žena".
 * Chybí, "", jiný text („zena“, „M“) → null (neznámé).
 */
export const usableGender = (value: unknown): "muž" | "žena" | null => {
  if (typeof value !== "string") return null;
  const upper = value.trim().toUpperCase();
  return upper === "MUŽ" ? "muž" : upper === "ŽENA" ? "žena" : null;
};

/**
 * Rok narození a pohlaví, které převod importu ponechá (rozhodnutí majitele
 * 12. 10. 2026: po registraci je mění jen správce, importy už nebudou).
 * Platnou hodnotu importu (usableBirthYear, usableGender) převod nepřepíše
 * formulářem; zapíše ji v kanonickém tvaru – rok jako číslo, pohlaví
 * "muž" / "žena" (jako registrace): aplikace i pravidla čtou pohlaví bez
 * ořezu mezer („žena “ by byla muž) a rok jako text místy jako neznámý věk.
 * Chybějící nebo neplatnou hodnotu doplní formulář. Pravidla
 * (legacyTransferProfile – transferKeepsLockedPersonal) pustí hodnotu
 * importu beze změny nebo týž kanonický tvar a platnost hodnotí stejně,
 * nanejvýš přísněji (mezery jen ASCII) – co klient ponechá, nezamítnou.
 */
export function lockedFromLegacy(legacy: DocumentData): DocumentData {
  const kept: DocumentData = {};
  const birthYear = usableBirthYear(legacy.birthYear);
  if (birthYear !== null) kept.birthYear = birthYear;
  const gender = usableGender(legacy.gender);
  if (gender !== null) kept.gender = gender;
  return kept;
}

/** Import pro dotaz needs-confirmation: id, jméno, rok narození a platné číslo. */
const candidateOf = (legacy: QueryDocumentSnapshot): LegacyCandidate => {
  const data = legacy.data();
  const seq = toSequenceInt(data.sequenceNumber);
  return {
    id: legacy.id,
    // Přípona malými („Jan Novák ml.“, ne „Ml.“) – nález 6 3. kola.
    name: formatPersonName(data.firstName, data.lastName),
    birthYear: usableBirthYear(data.birthYear),
    sequenceNumber: seq === null ? null : padSequenceNumber(seq),
  };
};

type LegacyDecision =
  | { kind: "transfer"; legacy: QueryDocumentSnapshot }
  | { kind: "new" }
  | { kind: "confirm"; candidates: LegacyCandidate[]; goneLegacyId?: string };

/**
 * Který import se převede (nález 8 2. kola: dřív první podle id dokumentu –
 * otec převzal profil syna, duplicita bez členství vyhrála nad tou se
 * členstvím; nálezy 1 a 3 3. kola: otec a syn se stejným jménem, zmizelý
 * vybraný import; nález 1 4. kola: import bez roku narození).
 *  - Volba střelce platí, i když jméno nesedí. Vybraný import, který
 *    mezitím zmizel, nic nepřevede ani nezaloží nového člena – vede na nový
 *    dotaz (goneLegacyId), i když už žádný jiný import nezbyl.
 *  - Bez volby a bez importů nový člen.
 *  - Bez volby převede jen import se shodným jménem včetně generační
 *    přípony ml./st. (nameMatchQuality) a se STEJNÝM rokem narození, který
 *    znají oba záznamy (usableBirthYear). Import bez použitelného roku
 *    (chybí, 0, "", datum, neceločíselný) se bez dotazu nepřevede ani se
 *    stejným jménem – může patřit jinému členovi rodiny se společným
 *    e-mailem (otec by tiše převzal číslo, členství a ZP syna). Mezi
 *    shodami přednost stejným slovům před jménem obsaženým v delším, pak
 *    platnému číslu, zaplacenému členství (nejnovější rok) a nakonec id.
 *  - Žádná taková shoda – ani u jediného importu – nic nepřevádí a ptá se
 *    (dotaz ukáže jméno, rok narození a číslo kandidáta; importy bez roku
 *    najdou správci Kontroly integrity).
 */
function chooseLegacy(
  transferable: QueryDocumentSnapshot[],
  pending: DocumentData,
  choice: RegistrationChoice | undefined
): LegacyDecision {
  if (choice && choice.kind === "new") return { kind: "new" };
  const candidates = () => transferable.map(candidateOf);
  if (choice) {
    const chosen = transferable.find((d) => d.id === choice.legacyId);
    return chosen
      ? { kind: "transfer", legacy: chosen }
      : { kind: "confirm", candidates: candidates(), goneLegacyId: choice.legacyId };
  }
  if (transferable.length === 0) return { kind: "new" };
  const myName = nameWords(pending.firstName, pending.lastName);
  const myYear = usableBirthYear(pending.birthYear);
  const matching = transferable
    .map((legacy) => {
      const data = legacy.data();
      return {
        legacy,
        // Rok narození musí znát oba záznamy a musí se shodovat: jiný rok =
        // jiný člověk i se stejným jménem (otec a syn), neznámý rok
        // stejného člověka nedokládá.
        sameYear: myYear !== null && usableBirthYear(data.birthYear) === myYear,
        quality: nameMatchQuality(myName, nameWords(data.firstName, data.lastName)),
        validNumber: isValidSequenceNumber(data.sequenceNumber) ? 1 : 0,
        paidYear: latestPaidYear(data),
      };
    })
    .filter((m) => m.sameYear && m.quality > 0);
  if (matching.length === 0) return { kind: "confirm", candidates: candidates() };
  matching.sort(
    (a, b) =>
      b.quality - a.quality ||
      b.validNumber - a.validNumber ||
      b.paidYear - a.paidYear ||
      (a.legacy.id < b.legacy.id ? -1 : a.legacy.id > b.legacy.id ? 1 : 0)
  );
  return { kind: "transfer", legacy: matching[0].legacy };
}

/**
 * Registrace (AuthPage): údaje formuláře do pendingProfiles/{uid}.
 * Transakce, ne setDoc: bez spojení selže (registrace se odroluje),
 * setDoc by offline jen čekal ve frontě.
 */
export async function savePendingProfile(
  uid: string,
  profile: PendingProfileInput
): Promise<void> {
  const ref = doc(db, PENDING_PROFILES_COLLECTION, uid);
  await runTransaction(db, async (tx) => {
    tx.set(ref, {
      firstName: profile.firstName,
      lastName: profile.lastName,
      birthYear: profile.birthYear,
      gender: profile.gender,
      nationality: profile.nationality,
      phone: profile.phone,
      email: profile.email,
      gdprConsentAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    });
  });
}

/** Profil v lokální cache (bez sítě); nic v cache nebo chyba → false (rozhodne server). */
async function profileInCache(ref: DocumentReference): Promise<boolean> {
  try {
    return (await getDocFromCache(ref)).exists();
  } catch {
    return false;
  }
}

// Pravidla párují podle e-mailu (dotaz na importovaný profil, převod) jen
// s ověřeným e-mailem v tokenu (email_verified) – token vydaný před ověřením
// obnoví ensureVerifiedToken (utils/emailVerification).

/**
 * Údaje nového profilu z rozpracovaného: osobní údaje formuláře, souhlas
 * a čas registrace. E-mail z tokenu – pravidla pustí jen profil s e-mailem
 * přihlášeného účtu (nález 11).
 */
function personalFrom(pending: DocumentData, email: string): DocumentData {
  const data: DocumentData = {};
  PERSONAL_FIELDS.forEach((key) => {
    if (pending[key] !== undefined) data[key] = pending[key];
  });
  data.email = email;
  data.gdprConsentAt =
    pending.gdprConsentAt !== undefined ? pending.gdprConsentAt : serverTimestamp();
  data.createdAt = pending.createdAt !== undefined ? pending.createdAt : serverTimestamp();
  return data;
}

/**
 * Má účet náhrobek smazaného profilu (DELETED_PROFILES_COLLECTION)?
 * Pravidla bez bloku deletedProfiles (před jejich nasazením) čtení odmítnou
 * – pak náhrobek ani vzniknout nemohl, takže false.
 */
async function hasTombstone(uid: string): Promise<boolean> {
  try {
    return (await getDocFromServer(doc(db, DELETED_PROFILES_COLLECTION, uid))).exists();
  } catch (err) {
    if (errorCode(err) !== "permission-denied") throw err;
    reportWarning("completeRegistration.tombstone", err, { uid });
    return false;
  }
}

/** Jak dlouho nejvýš čeká přihlášení na ostatní záložky (waitForSignedInFirestore). */
const SIGNED_IN_WAIT_MS = 10000;
/** Prodleva mezi pokusy waitForSignedInFirestore: první, pak dvojnásobná až po strop. */
const SIGNED_IN_FIRST_DELAY_MS = 250;
const SIGNED_IN_MAX_DELAY_MS = 2000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Po přihlášení: počká, až čtení Firestore chodí s novým přihlášením.
 *
 * S více otevřenými záložkami aplikace (persistentMultipleTabManager) čte
 * ze serveru jen jedna z nich – a s přihlášením, které má ona. Přihlášení
 * z jiné záložky převezme z IndexedDB až za chvíli (kolem 1 s, záložka na
 * pozadí i déle); do té doby čtení chráněná pravidly (rozpracovaný profil,
 * náhrobek, po finálních pravidlech i vlastní profil) končí „nemáte
 * oprávnění" a dokončení přihlášení by uživatele odhlásilo. Zkouší se proto
 * vlastní náhrobek (vlastník ho číst smí vždy, i když neexistuje), dokud
 * čtení hlásí permission-denied – nejvýš maxWaitMs, i když čtení visí. Jiná
 * chyba (offline) i vypršení: vrátí se a chybu ohlásí až samotné dokončení.
 * Nikdy nevyhodí.
 */
export async function waitForSignedInFirestore(
  uid: string,
  maxWaitMs: number = SIGNED_IN_WAIT_MS
): Promise<void> {
  const deadline = Date.now() + maxWaitMs;
  let delay = SIGNED_IN_FIRST_DELAY_MS;
  let attempts = 0;
  for (;;) {
    attempts += 1;
    const outcome = await Promise.race([
      getDocFromServer(doc(db, DELETED_PROFILES_COLLECTION, uid)).then(
        () => "ok",
        (err: unknown) => (errorCode(err) === "permission-denied" ? "denied" : "other")
      ),
      sleep(Math.max(deadline - Date.now(), 0)).then(() => "timeout"),
    ]);
    if (outcome === "ok" || outcome === "other") return;
    if (outcome === "timeout" || deadline - Date.now() <= delay) break;
    await sleep(delay);
    delay = Math.min(delay * 2, SIGNED_IN_MAX_DELAY_MS);
  }
  reportWarning(
    "completeRegistration.waitForSignedIn",
    "Firestore po přihlášení dál odmítá čtení – jiná záložka aplikace přihlášení nepřevzala.",
    { uid, attempts }
  );
}

async function runCompletion(
  user: User,
  choice: RegistrationChoice | undefined
): Promise<CompletionResult> {
  const uid = user.uid;
  const profileRef = doc(db, "users", uid);

  // 1) Profil už existuje – každé další přihlášení a účty ze starého toku.
  //    Nejdřív z cache (start aplikace nečeká na síť), pak ze serveru.
  if (await profileInCache(profileRef)) return { status: "existing" };
  const profileSnap = await inStep("profile", () => getDocFromServer(profileRef));
  if (profileSnap.exists()) return { status: "existing" };

  await inStep("token", () => ensureVerifiedToken(user));

  // 2) Údaje z registračního formuláře. Bez nich profil založit nejde
  //    (účet ze starého toku, kterému se profil nezapsal, registrace, které
  //    selhalo uložení i odvolání účtu, nedokončené smazání účtu).
  const pendingRef = doc(db, PENDING_PROFILES_COLLECTION, uid);
  const pendingSnap = await inStep("pending", () => getDocFromServer(pendingRef));
  // Trvalý super admin nemusí mít profil (pravidla ho poznají podle
  // ověřeného e-mailu, AdminOverview ho doplní do seznamu) – nesmí se
  // zamknout mimo aplikaci, ani náhrobkem.
  const superAdmin = user.email === PERMANENT_SUPER_ADMIN_EMAIL;
  if (!pendingSnap.exists()) {
    // Registraci mohla mezitím dokončit jiná záložka nebo zařízení –
    // rozpracovaný profil maže v téže transakci, ve které profil zakládá
    // (nález 11 2. kola). Proto profil ještě jednou ze serveru; čtení navíc
    // jen v této větvi.
    const again = await inStep("profile", () => getDocFromServer(profileRef));
    if (again.exists()) return { status: "existing" };
    if (superAdmin) return { status: "superAdmin" };
  }
  // Smazání účtu se nedokončilo (profil smazán, deleteUser selhal) –
  // AuthPage nabídne dokončení smazání. I s rozpracovaným profilem (nález 2
  // 3. kola): smazání účtu ho dřív nemazalo, takže by se z něj založil nový
  // profil (přechodová pravidla), resp. dokončení skončilo odmítnutím
  // a účet by zůstal zamčený (finální). Čtení navíc jen u účtu bez profilu.
  if (!superAdmin && (await inStep("pending", () => hasTombstone(uid)))) {
    throw new RegistrationCompletionError(
      "pending",
      codedError("lks/account-deleted", "Smazání účtu se nedokončilo (deletedProfiles).")
    );
  }
  if (!pendingSnap.exists()) {
    // Bez náhrobku i bez rozpracovaného profilu – AuthPage nabídne doplnění
    // údajů.
    throw new RegistrationCompletionError(
      "pending",
      codedError(
        "lks/registration-incomplete",
        "Účet nemá profil ani rozpracovanou registraci (pendingProfiles)."
      )
    );
  }
  const pending = pendingSnap.data();
  const email = user.email || "";
  const personal = personalFrom(pending, email);

  // 3) Admin-importované profily se stejným e-mailem – pravidla pustí jen
  //    dotaz where('email', '==', e-mail z tokenu), a to s ověřeným e-mailem
  //    (přesná shoda; e-maily v users sjednotila Příprava dat). Ze serveru:
  //    zastaralá cache by minula čerstvě importovaný záznam. docs se čte
  //    jednou – getter vrací pokaždé nové objekty (nález 39). Který se
  //    převede, rozhodne jméno nebo střelec (chooseLegacy).
  const candidates = email
    ? await inStep("legacy", async () => {
        const snap = await getDocsFromServer(
          query(collection(db, "users"), where("email", "==", email))
        );
        return snap.docs.filter((d) => d.id !== uid);
      })
    : [];
  const decision = chooseLegacy(
    candidates.filter((d) => isTransferableProfile(d.data())),
    pending,
    choice
  );
  if (decision.kind === "confirm") {
    return decision.goneLegacyId
      ? {
          status: "needs-confirmation",
          candidates: decision.candidates,
          goneLegacyId: decision.goneLegacyId,
        }
      : { status: "needs-confirmation", candidates: decision.candidates };
  }
  const legacyDoc = decision.kind === "transfer" ? decision.legacy : null;
  const skipped = candidates.filter((d) => !legacyDoc || d.id !== legacyDoc.id);
  if (skipped.length > 0) {
    // Profil správce, import s jiným jménem (otec/syn se společným e-mailem,
    // střelec odpověděl „nejsem to já“) nebo duplicitní import zůstává, jak
    // je – k ručnímu sloučení správcem (registrace kvůli němu nesmí spadnout).
    reportWarning(
      "completeRegistration.legacySkipped",
      "Profil se stejným e-mailem se nepřevádí (role správce, jiné jméno nebo duplicita).",
      {
        skipped: skipped.map((d) => d.id).join(","),
        legacyId: legacyDoc ? legacyDoc.id : "",
      }
    );
  }

  if (!legacyDoc) {
    // 4a) Nový člen: číslo z počítadla, profil a smazání rozpracovaného
    //     profilu v JEDNÉ transakci (zapíše se všechno, nebo nic).
    const sequenceNumber = await inStep("create", () =>
      createMemberProfile(uid, { ...personal, membership: {}, role: "user" }, (tx) => {
        tx.delete(pendingRef);
      })
    );
    return sequenceNumber === null
      ? { status: "existing" }
      : { status: "created", sequenceNumber };
  }

  // 4b) Převod importovaného profilu na nové uid: zachová členství, pořadové
  //     číslo, ZP, zákaz nováčka a platný rok narození a pohlaví
  //     (lockedFromLegacy); ostatní osobní údaje přepíše formulář. Role
  //     'user' (jinou roli z importu přidělí správce znovu). JEDNA transakce:
  //     nový profil (migratedFrom = id starého), záloha users_backup/{id} =
  //     starý dokument PŘESNĚ jak byl přečtený, smazání starého a smazání
  //     rozpracovaného profilu (+ nové číslo z počítadla, když staré není
  //     platné) – pravidla převod pustí jen pohromadě. Cizí profil v transakci
  //     číst nejde (get jen vlastní) – shodu s uloženým originálem hlídají
  //     pravidla.
  const legacyId = legacyDoc.id;
  const legacyData = legacyDoc.data();
  const keepNumber = isValidSequenceNumber(legacyData.sequenceNumber);
  const locked = lockedFromLegacy(legacyData);
  const sequenceNumber = await inStep("transfer", () =>
    runTransactionRetrying(async (tx) => {
      const existing = await tx.get(profileRef);
      if (existing.exists()) return null;
      const transferred: DocumentData = {
        ...legacyData,
        ...personal,
        ...locked,
        role: "user",
        migratedFrom: legacyId,
      };
      // Importované záznamy mívají prázdné/„undefined" číslo – bez platného
      // čísla by nešlo párovat platby (VS).
      if (!keepNumber) transferred.sequenceNumber = await takeSequenceNumber(tx);
      tx.set(profileRef, transferred);
      tx.set(doc(db, "users_backup", legacyId), legacyData);
      tx.delete(doc(db, "users", legacyId));
      tx.delete(pendingRef);
      return String(transferred.sequenceNumber);
    })
  );
  if (sequenceNumber === null) return { status: "existing" };
  // Registrace uvedla jiný rok narození nebo pohlaví, než má import – platí
  // import (opraví ho správce). AuthPage to střelci řekne (i u převodu bez
  // dotazu podle jména a roku, kde se může lišit pohlaví); diagnostika jen
  // názvy polí, ne hodnoty (osobní údaje).
  const overruled: LockedField[] = [];
  if ("birthYear" in locked && usableBirthYear(personal.birthYear) !== locked.birthYear) {
    overruled.push("birthYear");
  }
  if ("gender" in locked && usableGender(personal.gender) !== locked.gender) {
    overruled.push("gender");
  }
  if (overruled.length > 0) {
    reportWarning(
      "completeRegistration.legacyPersonalKept",
      "Rok narození / pohlaví z registrace se liší od importu – převod ponechal import.",
      { legacyId, fields: overruled.join(",") }
    );
  }
  if (!keepNumber) {
    reportWarning(
      "completeRegistration.legacySequence",
      "Importovaný záznam bez platného pořadového čísla – přiděleno nové.",
      { legacyId, legacySeq: String(legacyData.sequenceNumber), seq: sequenceNumber }
    );
  }
  return overruled.length > 0
    ? { status: "transferred", legacyId, sequenceNumber, overruled }
    : { status: "transferred", legacyId, sequenceNumber };
}

/**
 * Rozběhnutá dokončení podle uid a volby – přihlášení i efekt AuthPage
 * sdílí jeden běh; odpověď na dotaz (jiná volba) je nový běh.
 */
const inFlight = new Map<string, Promise<CompletionResult>>();

const runKey = (uid: string, choice: RegistrationChoice | undefined): string =>
  `${uid}|${!choice ? "" : choice.kind === "new" ? "new" : `transfer:${choice.legacyId}`}`;

/**
 * Přihlášený uživatel s OVĚŘENÝM e-mailem: zajistí profil users/{uid}.
 * Existuje → nic. Jinak ho založí z rozpracovaného profilu pendingProfiles
 * (převod importu se stejným e-mailem, shodným jménem a rokem narození,
 * nebo nový člen s číslem z počítadla) a rozpracovaný profil smaže. Import
 * se stejným e-mailem, ale jiným jménem nebo rokem, nebo bez roku
 * narození → needs-confirmation (nic se nezapíše); odpověď střelce přijde
 * jako choice. Chyba → RegistrationCompletionError (krok + kód; hláška
 * completionErrorMessage; lks/account-deleted a lks/registration-incomplete
 * řeší AuthPage vlastní obrazovkou); nic se nezapíše a příští přihlášení to
 * zkusí znovu.
 * Souběžná volání pro totéž uid a volbu sdílí jeden běh.
 */
export function completeRegistration(
  user: User,
  choice?: RegistrationChoice
): Promise<CompletionResult> {
  const key = runKey(user.uid, choice);
  const running = inFlight.get(key);
  if (running) return running;
  const started = runCompletion(user, choice);
  inFlight.set(key, started);
  const forget = () => {
    if (inFlight.get(key) === started) inFlight.delete(key);
  };
  started.then(forget, forget);
  return started;
}
